import { useMemo } from "react";

import { defaultPrivateSpaceName, defaultTeamSpaceName } from "../config";
import type { DisplayUser, ProjectID, ProjectRole, TeamID } from "../types";
import { type ProjectData, useProject } from "./useProject";
import { useWebExport, type WebExportData } from "./useWebExport";

export type UnifiedProjectAndWebExportData = {
  teamId: TeamID;
  projectId: ProjectID;
  projectName: string;
  spaceName?: string;
  lastModifiedAt: Date;
  createdByUser?: DisplayUser;
  userRole: ProjectRole;
  thumbnailUrl?: string;
};
function unifyProjectAndWebExport(
  projectData?: ProjectData,
  webExportData?: WebExportData
): UnifiedProjectAndWebExportData | undefined {
  if (projectData) {
    const createdByUser = projectData?.users?.find(
      (user) => user.userId === projectData.latestRevision.uploadedByUserId
    );

    const spaceName =
      projectData.space?.spaceName ?? spaceTypeNameMap[projectData.space?.spaceType ?? ""];

    return {
      teamId: projectData.teamId,
      projectId: projectData.project.projectId,
      projectName: projectData.project.name,
      spaceName,
      lastModifiedAt: projectData.latestRevision.lastModifiedAt,
      createdByUser: createdByUser,
      userRole: projectData.userRole,
      thumbnailUrl: projectData.latestRevision.thumbnailDownloads?.light?.url,
    };
  }

  if (webExportData) {
    const createdByUser = {
      userId: webExportData.webExport.uploadedByUserId,
    };

    return {
      teamId: webExportData.teamId,
      projectId: webExportData.webExport.projectId,
      projectName: webExportData.webExport.title,
      spaceName: undefined,
      lastModifiedAt: webExportData.webExport.uploadedAt,
      createdByUser: createdByUser,
      userRole: webExportData.userRole,
      thumbnailUrl: webExportData.revision?.thumbnailDownloads?.light?.url,
    };
  }

  return undefined;
}

const spaceTypeNameMap: Record<string, string> = {
  private: defaultPrivateSpaceName,
  team: defaultTeamSpaceName,
};

export const useProjectOrWebExport = () => {
  const { data: projectData, isLoading: isProjectLoading } = useProject();
  const { data: webExportData, isLoading: isWebExportLoading } = useWebExport();
  return useMemo(() => {
    return {
      data: unifyProjectAndWebExport(projectData, webExportData),
      isLoading: isProjectLoading || isWebExportLoading,
    };
  }, [projectData, webExportData, isProjectLoading, isWebExportLoading]);
};
